import { gql, useMutation } from '@apollo/client';
import {
  Button,
  ButtonGroup,
  FormErrorMessage,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
} from '@apollo/orbit';
import React, { useCallback, useState } from 'react';

import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { NotificationStatus } from 'src/lib/graphqlTypes/types';

export const SetProposalNotificationStatusModalServiceFragment = gql`
  fragment SetProposalNotificationStatusModalServiceFragment on Service {
    id
    name
    proposalNotificationStatus
  }
`;

export const SetProposalNotificationStatusModal = ({
  graph,
  isOpen,
  onClose,
}: {
  graph: GraphQLTypes.SetProposalNotificationStatusModalServiceFragment;
  isOpen: boolean;
  onClose: () => void;
}) => {
  const [errorMessage, setErrorMessage] = useState<string>();
  const [setNotificationStatus, { loading }] = useMutation<
    GraphQLTypes.SetProposalNotificationStatusMutation,
    GraphQLTypes.SetProposalNotificationStatusMutationVariables
  >(gql`
    mutation SetProposalNotificationStatusMutation(
      $graphId: ID!
      $input: SetProposalNotificationStatusInput!
    ) {
      service(id: $graphId) {
        setProposalNotificationStatus(input: $input) {
          ... on Service {
            id
            proposalNotificationStatus
          }
          ... on ValidationError {
            message
          }
          ... on PermissionError {
            message
          }
        }
      }
    }
  `);

  const notificationsEnabled =
    graph.proposalNotificationStatus === NotificationStatus.ALL;

  const handleClose = useCallback(() => {
    onClose();
    setErrorMessage('');
  }, [onClose]);

  const onSubmit = useCallback(async () => {
    const { data: responseData } = await setNotificationStatus({
      variables: {
        graphId: graph.id,
        input: {
          status: notificationsEnabled
            ? NotificationStatus.NONE
            : NotificationStatus.ALL,
        },
      },
    });

    if (
      responseData?.service?.setProposalNotificationStatus.__typename ===
      'Service'
    ) {
      onClose();
      setErrorMessage('');
    } else {
      setErrorMessage(
        responseData?.service?.setProposalNotificationStatus.message,
      );
    }
  }, [graph.id, notificationsEnabled, onClose, setNotificationStatus]);

  return (
    <Modal size="lg" isOpen={isOpen} onClose={handleClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <ModalTitle>
            {notificationsEnabled
              ? "Turn off notifications for this graph's proposals?"
              : "Turn on notifications for this graph's proposals?"}
          </ModalTitle>
        </ModalHeader>
        <ModalBody>
          {!!errorMessage && (
            <FormErrorMessage className="mb-2">{errorMessage}</FormErrorMessage>
          )}
          <p>
            {notificationsEnabled ? (
              <>
                You'll stop receiving emails for proposals related to{' '}
                <span className="font-semibold">{graph.name}</span> that you've
                authored or participated in (by commenting, reviewing, or saving
                revisions.)
              </>
            ) : (
              <>
                You'll only receive email notifications for proposals related to{' '}
                <span className="font-semibold">{graph.name}</span> that you've
                authored or participated in (by commenting, reviewing, or saving
                revisions.)
              </>
            )}
          </p>
        </ModalBody>
        <ModalFooter>
          <ButtonGroup>
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={onSubmit}
              isLoading={loading}
              loadingText={notificationsEnabled ? 'Disabling' : 'Enabling'}
            >
              {notificationsEnabled
                ? 'Yes, disable notifications'
                : 'Enable notifications'}
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
